import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Heart, Star, MapPin, ArrowRight } from 'lucide-react';
import { useFooterGlow } from '../context/FooterGlowContext';
import './Home.css';

const Home = () => {
  const { triggerGlow } = useFooterGlow();

  return (
    <div className="home">
      {/* Hero Section */}
      <section 
        className="hero"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/bookshelf-hero.jpg)`
        }}
      >
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Capturing Moments That Create
              <span className="highlight"> Nostalgia</span>
            </h1>
            <p className="hero-subtitle">
              Professional photography services in Scottsdale and Phoenix, Arizona. 
              Creating timeless images that you'll cherish for years to come.
            </p>
            <div className="hero-buttons">
              <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
                Book Your Session
                <ArrowRight size={20} />
              </Link>
              <Link to="/portraits" className="btn btn-outline">
                View Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Our Services</h2>
            <p>Professional photography tailored to your needs</p>
          </motion.div>

          <div className="services-grid">
            <motion.div 
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="service-icon">
                <Heart size={40} />
              </div>
              <h3>Portrait Photography</h3>
              <p>Beautiful outdoor portraits that capture your personality and create lasting memories.</p>
              <Link to="/portraits" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div 
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="service-icon">
                <Star size={40} />
              </div>
              <h3>Company Headshots</h3>
              <p>Professional headshots that make a lasting impression for your business and career.</p>
              <Link to="/headshots" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div 
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="service-icon">
                <Camera size={40} />
              </div>
              <h3>Real Estate Photography</h3>
              <p>Stunning property photography that showcases homes in their best light.</p>
              <Link to="/real-estate" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section section">
        <div className="container">
          <div className="about-content">
            <motion.div 
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Vellichor Visions?</h2>
              <p>
                The word "vellichor" describes the nostalgic feeling of being in a bookstore - 
                that warm, comforting sense of memories and stories. At Vellichor Visions, 
                we capture photographs that will give you that same feeling years from now.
              </p>
              <p>
                Based in Scottsdale and serving the greater Phoenix area, we specialize in 
                creating timeless images that tell your story. Whether it's a family portrait, 
                professional headshot, or real estate listing, we approach every session with 
                creativity, professionalism, and a passion for capturing authentic moments.
              </p>
              <div className="about-features">
                <div className="feature">
                  <MapPin size={20} />
                  <span>Serving Scottsdale & Phoenix</span>
                </div>
                <div className="feature">
                  <Camera size={20} />
                  <span>No Studio Required</span>
                </div>
                <div className="feature">
                  <Heart size={20} />
                  <span>Personalized Experience</span>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="about-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/CP101810.jpg`}
                alt="Beautiful portrait photography"
                className="about-portrait-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Create Timeless Memories?</h2>
            <p>Contact us for a personalized quote and let's start planning your perfect photo session.</p>
            <div className="cta-buttons">
              <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
                Get Your Quote
              </Link>
              <a href="mailto:vellichorvisions@gmail.com" className="btn btn-outline">
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 